import React from 'react';
import BaseComponent from 'src/components/BaseComponent';
import {
  lang,
  permissions,
  scssVariables as variables,
  upperFirstChar,
  withRouter
} from 'src/helpers';
import { SearchTextField, MCButton, PopoverMenu, View, TabMenu } from 'src/components/basic';
import { TableLibrary } from 'src/components/complex';
import SVGIcon, { BASE_ICONS } from 'src/components/svg';
import { remCss } from 'src/helpers';
import model from './model';
import moment from 'moment/moment';
import AlertManager from 'src/components/alert/AlertManager';
import ShortInfo from 'src/components/user/ShortInfo';
import LANG_CONST from 'src/helpers/lang/const';
import invitationsModel from './invitationsModel';
import { EmailPhone } from 'src/components/Form';

const { PAGES } = permissions;

class Patients extends BaseComponent {
  componentDidMount() {
    model.subscribe(this.subscription);
    invitationsModel.subscribe(this.subscription);
  }

  componentWillUnmount() {
    model.unsubscribe(this.subscription);
    invitationsModel.unsubscribe(this.subscription);
  }
  onSearchTextChange = (text, model) => {
    model.setStateSilent({ search: text, pageIndex: 0 });
    model.handleChangeSearch(text);
    if (!text) {
      model.setListStateSilent({ items: [], total: 0 }, 'search');
      model.refresh();
    }
  };

  renderSearchHeader = (model) => {
    return (
      <View
        style={{
          flexDirection: 'row',
          marginBottom: remCss(15),
          gap: remCss(15),
          flexWrap: 'wrap'
        }}
      >
        <SearchTextField
          template={'2'}
          width={300}
          placeholder={LANG_CONST.search}
          onChange={(text) => {
            this.onSearchTextChange(text, model);
          }}
          value={model.input('search')}
        />
        <View style={{ flex: 1 }} />
        {permissions.PAGES.PATIENTS_ADD_PAGE.isActive && (
          <MCButton
            type={'link'}
            primary
            dark
            radius
            medium
            to={permissions.PAGES.PATIENTS_ADD_PAGE.match()}
            style={{ height: remCss(30) }}
          >
            {lang.t('Add new')}
          </MCButton>
        )}
      </View>
    );
  };

  renderPatients() {
    let loading = model.refreshing();
    let noResults = model.noResults();
    let searchQuery = model.listInput('search');
    let items = model.items();
    let { total = 0 } = model.getCurrent();
    const medium = !this.props.screenDetail?.isLargeScreen;

    const columns = [
      {
        label: LANG_CONST.name,
        sort: { sortKey: 'name' },
        resize: true,
        renderCell: (item) => {
          return (
            <ShortInfo
              user={item}
              to={permissions.profilePath(item)}
              vertical={medium}
              description={medium && <EmailPhone item={item} emailWeight={400} />}
            />
          );
        }
      },
      !medium && {
        label: LANG_CONST.email_phone,
        resize: true,
        minWidth: remCss(250),
        width: '18%',
        renderCell: (item) => <EmailPhone item={item} />
      },
      {
        label: LANG_CONST.added_date,
        resize: true,
        sort: { sortKey: 'created' },
        minWidth: remCss(150),
        width: '15%',
        renderCell: (item) => (item.addDate ? moment(item.addDate).format('DD.MM.YYYY') : null)
      },
      {
        label: LANG_CONST.last_appointment,
        resize: true,
        sort: { sortKey: 'lastAppointment' },
        minWidth: remCss(180),
        width: '15%',
        renderCell: (item) =>
          item.lastAppointment ? moment(item.lastAppointment).format('DD - MMMM - YYYY') : ''
      },
      {
        label: LANG_CONST.status,
        resize: true,
        minWidth: '120',
        width: '15%',
        renderCell: (item) => (
          <button className={'mc-status-' + item.invitationStatus}>
            {lang.t(upperFirstChar(item.invitationStatus))}
          </button>
        )
      },
      !medium && {
        label: LANG_CONST.actions,
        cellStyles: { textAlign: 'end' },
        minWidth: remCss(270),
        width: '20%',
        renderCell: this.renderPatientActions
      }
    ].filter((i) => !!i);

    return (
      <>
        {this.renderSearchHeader(model)}
        <TableLibrary
          className={medium ? 'mc-row2' : ''}
          renderExtend={medium && this.renderPatientActions}
          loading={loading}
          noResults={noResults}
          searchQuery={searchQuery}
          sortBy={model.state.sortBy}
          selectedPage={model.state.pageIndex}
          total={total}
          limit={model.listLimit}
          onSortChange={(sortBy) => {
            model.handleChange('sortBy', sortBy);
            model.setListState({ items: [] }, model.getCurrentId(), null, true);
            model.loadMore();
          }}
          onSetPage={(v) => {
            model.handleChange('pageIndex', v);
            model.setListState({ items: [] }, model.getCurrentId(), null, true);
            model.loadMore();
          }}
          key={medium ? 1 : 0}
          columns={columns}
          data={items}
          otherProps={{
            cell: () => ({
              style: {
                alignItems: 'flex-start'
              }
            })
          }}
        />
      </>
    );
  }

  renderPatientActions = (item) => {
    const canEdit = permissions.PAGES.PATIENTS_ADD_PAGE.checkActive({
      auth: model.getUser()
    });
    let canEditUserIds = [
      model.getUser()?.id,
      model.getUser()?.managerToClinic,
      ...(model.getUser()?.clinicIds || [])
    ];

    return (
      <div className={'mc-flex'} style={{ justifyContent: 'flex-end' }}>
        {permissions.PAGES.PATIENTS_ANALYSE_PAGE.isActive && (
          <>
            <MCButton
              type={'link'}
              label={LANG_CONST.titles.analyses}
              to={permissions.PAGES.PATIENTS_ANALYSE_PAGE.match(item?.id)}
              primary
              dark
              radius
            />
          </>
        )}
        <div className={'mc-flex mc-grow'} style={{ justifyContent: 'flex-end' }}>
          {canEdit && item.status === 'inactive' && canEditUserIds.includes(item.createdBy) && (
            <MCButton
              type={'link'}
              iconname={BASE_ICONS.EDIT}
              to={PAGES.PATIENTS_EDIT_PAGE.match(item.id)}
              primary
              style={{ minWidth: remCss(42) }}
              light
              radius
              padding={5}
              width={42}
            />
          )}
          <MCButton
            type={'link'}
            iconname={BASE_ICONS.EYE_FILL}
            to={permissions.PAGES.PATIENTS_DETAIL_PAGE.match(item?.id)}
            width={42}
            dark
            padding={5}
            radius
            fillOpacity={'0.5'}
          />

          {canEdit && (
            <View style={{ minWidth: remCss(24) }}>
              <PopoverMenu
                arrowOffset={30}
                placement={'bottom-end'}
                popupMenuStyle={{
                  backgroundColor: 'white',
                  padding: remCss(10),
                  overflow: 'none'
                }}
                menu={() => (
                  <>
                    <MCButton
                      iconname={BASE_ICONS.DELETE}
                      width={42}
                      light
                      radius
                      secondary
                      padding={5}
                      onClick={(e) => {
                        e.preventDefault();
                        AlertManager.popup(AlertManager.TYPES.WARN, {
                          close: false,
                          title: LANG_CONST.notice.delete,
                          cancelButton: {
                            label: LANG_CONST.buttons.no,
                            action: (actions) => actions.hide()
                          },
                          actionButton: {
                            label: LANG_CONST.buttons.yes,
                            action: (actions) => {
                              actions.setLoading(true);
                              model.remove(item, () => {
                                actions.setLoading(false);
                                actions.hide();
                              });
                            }
                          }
                        });
                      }}
                    />
                  </>
                )}
                className={'mc-pointer mc-flex mc-flex-center'}
              >
                {() => <SVGIcon iconname={BASE_ICONS.DOTS} width={24} />}
              </PopoverMenu>
            </View>
          )}
        </div>
      </div>
    );
  };

  renderInvitations = () => {
    let loading = invitationsModel.refreshing();
    let noResults = invitationsModel.noResults();
    let searchQuery = invitationsModel.listInput('search');
    let items = invitationsModel.items();
    let { total = 0 } = invitationsModel.getCurrent();
    const medium = !this.props.screenDetail?.isLargeScreen;

    const columns = [
      {
        label: LANG_CONST.name,
        resize: true,
        renderCell: (item) => (
          <div className={'mc-flex-5-col'}>
            <ShortInfo
              user={item}
              fullname={item.fullname}
              vertical={medium}
              description={medium && <EmailPhone item={item} emailWeight={400} />}
            />
          </div>
        )
      },
      !medium && {
        label: LANG_CONST.email_phone,
        resize: true,
        minWidth: '250',
        width: '25%',
        renderCell: (item) => <EmailPhone item={item} />
      },
      {
        label: LANG_CONST.added_date,
        resize: true,
        minWidth: '150',
        width: '20%',
        renderCell: (item) => (item.date ? moment(item.date).format('DD.MM.YYYY') : null)
      },
      {
        label: LANG_CONST.status,
        resize: true,
        minWidth: '120',
        width: '15%',
        renderCell: (item) => (
          <button className={'mc-status-' + item.status}>
            {lang.t(upperFirstChar(item.status))}
          </button>
        )
      },
      {
        label: LANG_CONST.actions,
        cellStyles: { textAlign: 'end' },
        minWidth: '150',
        width: '10%',
        renderCell: (item) => {
          const canEdit = permissions.PAGES.PATIENTS_ADD_PAGE.checkActive({
            auth: model.getUser(),
            doctor: item
          });
          return (
            <div className={'mc-flex'} style={{ justifyContent: 'flex-end' }}>
              {item.status !== 'accepted' && (
                <MCButton
                  primary
                  light
                  radius
                  medium
                  padding={1}
                  style={{
                    paddingHorizontal: remCss(9),
                    paddingVertical: remCss(5.5),
                    fontWeight: 400
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    AlertManager.popup(AlertManager.TYPES.INFO, {
                      close: false,
                      title: LANG_CONST.notice.resend,
                      cancelButton: {
                        label: LANG_CONST.buttons.no,
                        action: (actions) => actions.hide()
                      },
                      actionButton: {
                        label: LANG_CONST.buttons.yes,
                        action: (p) => {
                          p.setLoading(true);
                          invitationsModel.resend(item.id, (err) => {
                            p.setLoading(false);
                            if (!err)
                              AlertManager.popup(AlertManager.TYPES.INFO, {
                                close: false,
                                width: 450,
                                title: lang.t(LANG_CONST.notice.sent_invitation, {
                                  type: lang.t(LANG_CONST.patient).toLowerCase()
                                }),
                                actionButton: {
                                  label: LANG_CONST.buttons.ok,
                                  action: (actions) => {
                                    actions.hide();
                                  }
                                }
                              });
                          });
                        }
                      }
                    });
                  }}
                >
                  <span style={{ fontWeight: 400, fontFamily: variables['fonts-mc-regular'] }}>
                    {lang.t(LANG_CONST.buttons.resend)}
                  </span>
                </MCButton>
              )}
              {canEdit && (
                <View style={{ minWidth: remCss(24) }}>
                  <PopoverMenu
                    arrowOffset={30}
                    placement={'bottom-end'}
                    popupMenuStyle={{
                      backgroundColor: 'white',
                      padding: remCss(10),
                      overflow: 'none'
                    }}
                    menu={() => (
                      <>
                        <MCButton
                          iconname={BASE_ICONS.DELETE}
                          width={42}
                          light
                          radius
                          secondary
                          padding={5}
                          onClick={(e) => {
                            e.preventDefault();
                            AlertManager.popup(AlertManager.TYPES.WARN, {
                              close: false,
                              title: LANG_CONST.notice.delete,
                              cancelButton: {
                                label: LANG_CONST.buttons.no,
                                action: (actions) => actions.hide()
                              },
                              actionButton: {
                                label: LANG_CONST.buttons.yes,
                                action: (actions) => {
                                  actions.setLoading(true);
                                  invitationsModel.remove(item, () => {
                                    actions.setLoading(false);
                                    actions.hide();
                                  });
                                }
                              }
                            });
                          }}
                        />
                      </>
                    )}
                    className={'mc-pointer mc-flex mc-flex-center'}
                  >
                    {() => <SVGIcon iconname={BASE_ICONS.DOTS} width={24} />}
                  </PopoverMenu>
                </View>
              )}
            </div>
          );
        }
      }
    ].filter((c) => !!c);

    return (
      <>
        {this.renderSearchHeader(invitationsModel)}
        <TableLibrary
          otherProps={{
            cell: () => ({
              style: {
                alignItems: 'flex-start'
              }
            })
          }}
          loading={loading}
          noResults={noResults}
          searchQuery={searchQuery}
          sortBy={invitationsModel.state.sortBy}
          selectedPage={invitationsModel.state.pageIndex}
          total={total}
          limit={invitationsModel.listLimit}
          onSortChange={(sortBy) => {
            invitationsModel.handleChange('sortBy', sortBy);
            invitationsModel.setListState(
              { items: [] },
              invitationsModel.getCurrentId(),
              null,
              true
            );
            invitationsModel.loadMore();
          }}
          onSetPage={(v) => {
            invitationsModel.handleChange('pageIndex', v);
            invitationsModel.setListState(
              { items: [] },
              invitationsModel.getCurrentId(),
              null,
              true
            );
            invitationsModel.loadMore();
          }}
          columns={columns}
          data={items}
        />
      </>
    );
  };

  render() {
    return (
      <div className={'mc-container-content'}>
        <div className={'mc-container-content__header'}>
          <span className={'mc-grow'}>{lang.t(LANG_CONST.titles.patients)}</span>
        </div>
        <div className={'mc-container-content__body'} style={{ zIndex: 0 }}>
          <div className={'mc-container-content__body-header'}>
            <div className={'mc-grow'}>
              <TabMenu
                tabs={[
                  { id: 'patients', name: LANG_CONST.patients },
                  {
                    id: 'invitations',
                    name: LANG_CONST.invitations
                  }
                ]}
                tab={{ id: model.input('tab') }}
                setTab={(tab) => {
                  if (tab.id === model.input('tab')) {
                    model.setListStateSilent({ items: [] });
                    model.loadMore();
                  }
                  model.setState({ tab: tab.id });
                }}
                type={'panner'}
              />
            </div>
          </div>

          <div className={'mc-tab-box-opacity mc-grow'}>
            <div
              className={
                'mc-tab-content mc-flex-col ' +
                (model.input('tab') === 'patients' ? '' : 'mc-inactive')
              }
            >
              <div className={'mc-container-content__body-content'}>{this.renderPatients()}</div>
            </div>
            <div
              className={
                'mc-tab-content mc-flex-col ' +
                (model.input('tab') !== 'patients' ? '' : 'mc-inactive')
              }
            >
              <div className={'mc-container-content__body-content'}>{this.renderInvitations()}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default withRouter(Patients);
